/** Shared booking flow — pricing, package, and theme tokens */

export const GOLD = '#C9A84C';
export const BOOKING_GOLD = '#B8972A';
export const BG = '#090909';
export const BOOKING_BG = '#0A0A0A';
export const TEXT_MUTED = '#9ca3af';
export const CARD_BG = '#111111';
export const PACKAGE_CARD_BG = '#141414';
export const PANEL_BG = '#111111';
export const INPUT_BG = '#1a1a1a';
export const GOLD_RGBA = 'rgba(201, 168, 76';
export const BOOKING_GOLD_RGBA = 'rgba(184, 151, 42';

export const MIN_BOOKING_HOURS = 4;
export const DURATION_OPTIONS = [4, 5, 6, 7, 8];
export const EVENT_DURATION_HOURS = 4;

export const SERVICE_PACKAGE = {
  id: 'open-play',
  name: 'Open Play',
  baseHours: MIN_BOOKING_HOURS,
};

export const BOOKING_PACKAGES = [
  {
    id: 'open-play',
    name: 'Open Play',
    hook: 'Play at your own pace.',
    tag: 'Casual & Social',
    basePrice: 1749,
    hourlyRate: 0,
    baseHours: EVENT_DURATION_HOURS,
    flatRate: true,
    fixedDuration: EVENT_DURATION_HOURS,
    hourlyLabel: null,
    priceDescriptor: '4-hour event',
    description:
      'Open golf, mini games, and casual competition built for guests to jump in and play.',
    breakdown: [
      { label: 'Simulator time', amount: 1399 },
      { label: 'Transportation + staffing', amount: 350 },
    ],
    features: [
      'Open bay time',
      'All mini games included',
      'Full delivery, setup, and breakdown',
      'Premium enclosure, impact screen, turf, and launch monitor',
      'On-site Digital Divot host',
    ],
    bestFor:
      'Casual get-togethers, birthdays, public events, and events where guests can rotate in and play at their own pace.',
    includedAddOnIds: [],
  },
  {
    id: 'competitive-play',
    name: 'Competitive Play',
    hook: 'Compete for the leaderboard.',
    tag: 'Compete & Win',
    popular: true,
    popularLabel: 'Popular for Events',
    basePrice: 2249,
    hourlyRate: 0,
    baseHours: EVENT_DURATION_HOURS,
    flatRate: true,
    fixedDuration: EVENT_DURATION_HOURS,
    hourlyLabel: null,
    priceDescriptor: '4-hour event',
    description:
      'Turn the simulator into a structured competition complete with tournament management, a live leaderboard, and prizes.',
    breakdown: [
      { label: 'Simulator time', amount: 1399 },
      { label: 'Transportation + staffing', amount: 350 },
      { label: 'Prizes', amount: 500 },
    ],
    features: [
      'Everything needed for the four-hour simulator activation',
      'Facilitated tournament',
      'Live leaderboard',
      'Golf-related prizes for top finishers',
      'Flexible game format for different group sizes',
      'Digital Divot host managing the competition',
    ],
    bestFor:
      'Corporate events, team competitions, golf tournaments, brand activations, and groups looking for a more structured experience.',
    includedAddOnIds: [],
  },
  {
    id: 'skills-trainer',
    name: 'Skills Trainer',
    hook: 'Get professional instruction.',
    tag: 'Learn & Improve',
    basePrice: 2249,
    hourlyRate: 0,
    baseHours: EVENT_DURATION_HOURS,
    flatRate: true,
    fixedDuration: EVENT_DURATION_HOURS,
    hourlyLabel: null,
    priceDescriptor: '4-hour event',
    description: 'Add professional, hands-on golf instruction to the Digital Divot experience.',
    breakdown: [
      { label: 'Simulator time', amount: 1399 },
      { label: 'Transportation + staffing', amount: 350 },
      { label: 'Private coaching', amount: 500 },
    ],
    features: [
      'Everything needed for the four-hour simulator activation',
      'Hands-on coaching throughout the event',
      'Professional golf expert on-site',
      'Personalized instruction for different skill levels',
      'Launch-monitor data guests can use to understand their swing',
    ],
    bestFor:
      'Golf outings, client entertainment, beginner-friendly events, employee wellness events, and groups that want guests to leave with something they learned.',
    includedAddOnIds: [],
  },
];

/** Features sold only via package tier — never as standalone optional add-ons */
export const PACKAGE_FEATURE_ADDON_IDS = ['lounge', 'photography'];

export const BOOKING_ADD_ONS = [
  {
    id: 'photography',
    name: 'Photography',
    price: 0,
    priceLabel: 'TBD',
    description: 'Professional event photos to capture the action and the crowd.',
    packageFeature: true,
  },
  {
    id: 'lounge',
    name: 'Lounge Area Setup',
    price: 0,
    priceLabel: 'TBD',
    description: 'Extra comfort and ambiance with a dedicated spectator and chill zone.',
    packageFeature: true,
  },
];

export const isWeekendDate = (date) => {
  if (!date) return false;
  const day = date.getDay();
  return day === 0 || day === 5 || day === 6;
};

/** Legacy weekday/weekend pricing — superseded by package selection */
export const getServicePricing = (date) => {
  if (date && isWeekendDate(date)) {
    return { basePrice: 500, hourlyRate: 200, label: 'Weekend (Fri–Sun)' };
  }
  return { basePrice: 300, hourlyRate: 100, label: 'Weekday (Mon–Thu)' };
};

export const getPackageMinHours = (selectedPackage) =>
  selectedPackage?.fixedDuration ??
  selectedPackage?.baseHours ??
  MIN_BOOKING_HOURS;

export const calculateBookingPricing = (
  selectedPackage,
  durationHours,
  selectedAddOnsList = []
) => {
  const empty = {
    basePrice: 0,
    additionalHours: 0,
    additionalHoursCost: 0,
    hourlyRate: 0,
    addOnsTotal: 0,
    total: 0,
    deposit: 0,
    minHours: MIN_BOOKING_HOURS,
    packageName: '',
    isFlatRate: false,
  };

  if (!selectedPackage) return empty;

  const minHours = getPackageMinHours(selectedPackage);
  const effectiveDuration = selectedPackage.flatRate
    ? getPackageMinHours(selectedPackage)
    : Math.max(durationHours, minHours);

  if (selectedPackage.flatRate) {
    const total = selectedPackage.basePrice;
    return {
      basePrice: total,
      additionalHours: 0,
      additionalHoursCost: 0,
      hourlyRate: 0,
      addOnsTotal: 0,
      total,
      deposit: Math.round(total * 0.25),
      minHours,
      packageName: selectedPackage.name,
      isFlatRate: true,
    };
  }

  const additionalHours = Math.max(0, effectiveDuration - minHours);
  const additionalHoursCost = additionalHours * selectedPackage.hourlyRate;
  // Package price already covers inclusions; never bill lounge/photography separately
  const nonBillableIds = new Set([
    ...PACKAGE_FEATURE_ADDON_IDS,
    ...(selectedPackage.includedAddOnIds || []),
  ]);
  const addOnsTotal = selectedAddOnsList.reduce((sum, addon) => {
    if (!addon?.id || nonBillableIds.has(addon.id)) return sum;
    return sum + (addon.price || 0);
  }, 0);
  const total = selectedPackage.basePrice + additionalHoursCost + addOnsTotal;
  const deposit = Math.round(total * 0.25);

  return {
    basePrice: selectedPackage.basePrice,
    additionalHours,
    additionalHoursCost,
    hourlyRate: selectedPackage.hourlyRate,
    addOnsTotal,
    total,
    deposit,
    minHours,
    packageName: selectedPackage.name,
    isFlatRate: false,
  };
};

export const formatAddOnPrice = (addon) => {
  if (addon.priceLabel) return addon.priceLabel;
  if (addon.price > 0) return `$${addon.price}`;
  return 'TBD';
};

export const getBookingDurationHours = (bookingData) => {
  if (!bookingData) return MIN_BOOKING_HOURS;
  if (bookingData.durationHours != null) return bookingData.durationHours;
  const base = bookingData.selectedPackage?.baseHours ?? MIN_BOOKING_HOURS;
  return base + (bookingData.extraHours || 0);
};

export const getIncludedAddOns = (selectedPackage) => {
  if (!selectedPackage?.includedAddOnIds?.length) return [];
  return BOOKING_ADD_ONS.filter((addon) =>
    selectedPackage.includedAddOnIds.includes(addon.id)
  );
};

export const getOptionalAddOns = (selectedPackage) => {
  if (!selectedPackage) {
    return BOOKING_ADD_ONS.filter(
      (addon) => !PACKAGE_FEATURE_ADDON_IDS.includes(addon.id)
    );
  }
  if (selectedPackage.flatRate) return [];
  const included = selectedPackage.includedAddOnIds || [];
  return BOOKING_ADD_ONS.filter(
    (addon) =>
      !included.includes(addon.id) &&
      !PACKAGE_FEATURE_ADDON_IDS.includes(addon.id)
  );
};

/** Experience formats are distinct — not upgrade tiers. */
export const getPackageUpgradeOptions = () => [];

export const getPackageById = (packageId) =>
  BOOKING_PACKAGES.find((pkg) => pkg.id === packageId) || null;
